import { MyAreaNote, Type } from '@msc-b2c/interfaces/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  getActiveSession,
  getShipByCruiseId,
  myAreaAvailableServiceTypeInPageReset,
  replacePlaceholders,
  resolveDateFormat,
  RootState,
  triggerShowEmptyNote,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { GenericNotification } from '@msc-b2c/shared-components';
import {
  fetchMainNotesContentFragment,
  filterNotesByCruiseCriteria,
  getSortedNotes,
} from './utils/orderNotesUtilFunctions';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { CabinI, Note } from '@msc-b2c/interfaces/interfaces';
import { BookingStatusEnum, LoginMode } from '@msc-b2c/interfaces/enums';
import { getCabinByPassengerId, getDeviceType } from '../utils/utils';
import {
  cabinOfTheLoggedUser,
  getCabinIdOfTheLoggedUser,
  isB2CBooking,
  isPaymentEnabled,
  ROUTE_CHANGED,
} from '../utils/myAreaUtils';

export function OrderNotes({ orderNoteTypes, serviceTypes }: Readonly<MyAreaNote>) {
  const dispatch = useDispatch<AppDispatch>();
  const deviceType = getDeviceType();
  const allOBS = useSelector((state: RootState) => state.myCruise.obs?.onboardServices);
  const availableServiceTypesInPage = useSelector(
    (state: RootState) => state.myCruise.availableServiceTypesInPage
  );
  const showZeroResults = useSelector(
    (state: RootState) => state.myAreaNotifications.showZeroResultsNote
  );
  const prepaidType = useSelector((state: RootState) => state.myAreaCommonConfig.prepaidType);
  const zeroResultsNote = useSelector((state: RootState) =>
    prepaidType === 'OBS'
      ? state.myAreaNotifications.obsEmptyNote
      : state.myAreaNotifications.excursionsEmptyNote
  );
  const itineraryResponse = useSelector((state: RootState) => state.myCruise.itinerary);
  const optBannerExcursion = useSelector(
    (state: RootState) => state.myAreaNotifications.optExcursionBanner
  );
  const negativeBalanceNote = useSelector(
    (state: RootState) => state.myAreaNotifications.negativeBalanceNote
  );
  const showNegativeBalance = useSelector(
    (state: RootState) => state.myAreaNotifications.showNegativeBalanceNote
  );
  const selectedPortCode = useSelector(
    (state: RootState) => state.myCruise.selectedDayFilter?.portCode
  );

  //get departure date from itinerary
  const departureDate = itineraryResponse?.departureDate;
  const departurePort = itineraryResponse?.days?.[0]?.port?.code;

  const { bookingNumber = '', passengerId, loginMode } = getActiveSession();

  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);

  const cabinIDOfTheLoggedUser = getCabinIdOfTheLoggedUser(booking);
  const isB2BBooking = booking && !isB2CBooking(booking);
  const cabinLoggedUser: CabinI | undefined = cabinOfTheLoggedUser(
    booking?.cabins,
    cabinIDOfTheLoggedUser
  );
  const cruiseID = cabinLoggedUser?.cruise?.id;
  const shipCode = getShipByCruiseId(cruiseID);
  const area = cabinLoggedUser?.cruise?.demographicArea;
  const [mainNotes, setMainNotes] = useState<Note[] | null>(null);
  const [excursionNotes, setExcursionNotes] = useState<Note[] | null>(null);
  const [obsNote, setObsNote] = useState<Note[] | null>(null);

  // obs types should be populated only if the orderNoteTypes contains "obs"
  const obsTypes: (string | undefined)[] | undefined = orderNoteTypes.some(
    (ont) => ont.type === 'obs'
  )
    ? serviceTypes?.map((serviceType: Type) => {
        return serviceType.type;
      })
    : [];

  let shipName = '';
  let cruiseDepartureDate = '';
  let optionExpirationDate = '';
  let isOpt = false;
  if (booking) {
    isOpt = booking.status.code === BookingStatusEnum.OPTIONAL;
    const cabins: CabinI[] | undefined = booking?.cabins;
    if (cabins) {
      const findCabinByPassengerId: CabinI | undefined = getCabinByPassengerId(cabins, passengerId);
      if (findCabinByPassengerId) {
        shipName = findCabinByPassengerId.cruise.ship.name;
        cruiseDepartureDate = findCabinByPassengerId.cruise.departureDate;
        optionExpirationDate = booking?.optionExpirationDate;
      }
    }
  }

  useEffect(() => {
    const onRouteChange = (e: Event) => {
      dispatch(triggerShowEmptyNote(false));
      dispatch(myAreaAvailableServiceTypeInPageReset());
    };
    window.addEventListener(ROUTE_CHANGED, onRouteChange);
    return () => {
      window.removeEventListener(ROUTE_CHANGED, onRouteChange);
    };
  }, []);

  // get main note from content fragments, then apply cruise targeting in FE
  useEffect(() => {
    const fetchData = async () => {
      // Need cruise context to evaluate configured CF targeting rules
      if (!departureDate && !departurePort && !shipCode && !area && !cruiseID) {
        return;
      }

      const notesFromCF = await fetchMainNotesContentFragment(obsTypes);
      const matchedNotes = filterNotesByCruiseCriteria(notesFromCF, {
        cruiseArea: area,
        departurePort,
        shipCode,
        departureDate,
        cruiseId: cruiseID,
      });

      const mainNotesFromCF = matchedNotes.filter((note: Note) => note.noteType === 'main');
      setMainNotes(mainNotesFromCF);

      const excursionNotesFromCF = matchedNotes.filter(
        (note: Note) => note.noteType === 'excursion'
      );
      setExcursionNotes(excursionNotesFromCF);

      // exclude from obsTypes the ones that aren't included in the allOBS types,
      // so if there aren't any obs with that serviceType available, the note will not be shown
      const filteredObsTypes = obsTypes?.filter((type: string | undefined) => {
        return allOBS?.some((obs) => obs?.type?.code === type);
      });
      const obsNotesFromCF =
        filteredObsTypes.length > 0
          ? matchedNotes.filter((note: Note) => note.noteType === 'obs')
          : [];
      setObsNote(obsNotesFromCF);
    };
    fetchData();
  }, [cruiseID, allOBS, departureDate, departurePort, shipCode, area]);

  // Sort notes by priority order
  const sortedMainNotes = getSortedNotes(mainNotes);
  const sortedExcursionNotes = getSortedNotes(excursionNotes);
  const sortedObsNotes = getSortedNotes(obsNote);

  return (
    <>
      {orderNoteTypes.map((ont, index) => {
        // if it's "main" type, show the main notes
        if (
          ont.type === 'main' &&
          sortedMainNotes.length > 0 &&
          (loginMode === LoginMode.FULL || (loginMode === LoginMode.LIGHT && !ont.hideInLightLogin))
        ) {
          return sortedMainNotes.map((note, mainIdex) => (
            <GenericNotification
              key={`excursion-${index}-${ont.type}-${mainIdex}`}
              bannerTitle={note.title ?? ''}
              bannerIcon={note.icon ?? ''}
              bannerBackgroundColor={note.background ?? ''}
              bannerDescription={note.description?.html ?? ''}
              bannerTextColor={note.textColor ?? ''}
            />
          ));
        }

        // if it's "obs" type, show the section notes
        if (ont.type === 'obs' && sortedObsNotes.length > 0) {
          return sortedObsNotes.map((note, mainIdex) => (
            <GenericNotification
              key={`obs-${index}-${ont.type}-${mainIdex}`}
              bannerTitle={note.title ?? ''}
              bannerIcon={note.icon ?? ''}
              bannerBackgroundColor={note.background ?? ''}
              bannerDescription={note.description?.html ?? ''}
              bannerTextColor={note.textColor ?? ''}
            />
          ));
        }

        // if it's "excursions" type, show the  notes if the selected port code is the same as one of the port of call in the note
        // if no port of call is specified, the note is shown no matter the selected port code
        if (ont.type === 'excursion' && sortedExcursionNotes.length > 0) {
          return sortedExcursionNotes.map((note, excIndex) => {
            if (
              !note.portOfCall ||
              note.portOfCall.length === 0 ||
              (selectedPortCode && note.portOfCall.includes(selectedPortCode))
            ) {
              return (
                <GenericNotification
                  key={`excursion-${index}-${ont.type}-${excIndex}`}
                  bannerTitle={note.title ?? ''}
                  bannerIcon={note.icon ?? ''}
                  bannerBackgroundColor={note.background ?? ''}
                  bannerDescription={note.description?.html ?? ''}
                  bannerTextColor={note.textColor ?? ''}
                />
              );
            }
          });
        }

        // if it's "negative-balance" type and itinerary card balance is negative, show the negative balance note
        if (ont.type === 'negative-balance' && showNegativeBalance) {
          return (
            <GenericNotification
              key={`negative-balance-${index}-${ont.type}`}
              bannerTitle={negativeBalanceNote?.title ?? ''}
              bannerIcon={negativeBalanceNote?.icon ?? ''}
              bannerBackgroundColor={negativeBalanceNote?.backgroundColor ?? ''}
              bannerDescription={negativeBalanceNote?.description ?? ''}
              bannerTextColor={negativeBalanceNote?.textColor ?? ''}
            />
          );
        }

        // if it's "empty" type and showZeroResults is true, show the zero results note
        if (ont.type === 'empty' && showZeroResults && availableServiceTypesInPage?.length === 0) {
          return (
            <GenericNotification
              key={`empty-${index}-${ont.type}`}
              bannerTitle={zeroResultsNote?.title ?? ''}
              bannerIcon={zeroResultsNote?.icon ?? ''}
              bannerBackgroundColor={zeroResultsNote?.backgroundColor ?? ''}
              bannerDescription={zeroResultsNote?.description ?? ''}
              bannerTextColor={zeroResultsNote?.textColor ?? ''}
            />
          );
        }

        // if it's "opt" type and optBannerExcursion is true, show the opt banner
        if (
          ont.type === 'opt' &&
          optBannerExcursion &&
          isOpt &&
          optBannerExcursion.enabled &&
          booking &&
          ((loginMode === LoginMode.LIGHT && deviceType === 'desktop' && !ont.hideOnDesktop) ||
            (loginMode === LoginMode.LIGHT && deviceType === 'tablet' && !ont.hideOnTablet) ||
            (loginMode === LoginMode.LIGHT && deviceType === 'mobile' && !ont.hideOnMobile))
        ) {
          return (
            <GenericNotification
              key={`opt-${index}-${ont.type}`}
              bannerTitle={optBannerExcursion?.title ?? ''}
              bannerIcon={optBannerExcursion?.icon ?? ''}
              bannerBackgroundColor={optBannerExcursion?.backgroundColor ?? ''}
              bannerDescription={
                replacePlaceholders(
                  isB2BBooking
                    ? optBannerExcursion.descriptionB2B
                    : optBannerExcursion.description ?? '',
                  {
                    bookingNumber: bookingNumber,
                    shipName: shipName,
                    cruiseDepartureDate: cruiseDepartureDate
                      ? dayjs(cruiseDepartureDate).format(resolveDateFormat())
                      : '',
                    optionExpirationDate: optionExpirationDate
                      ? dayjs(optionExpirationDate).format(resolveDateFormat())
                      : '',
                  }
                ) ?? ''
              }
              bannerTextColor={optBannerExcursion?.textColor ?? ''}
              bannerButtonLabel={
                !isB2BBooking && isPaymentEnabled(booking)
                  ? optBannerExcursion?.buttonLabel ?? ''
                  : ''
              }
              bannerButtonLink={
                !isB2BBooking && isPaymentEnabled(booking)
                  ? optBannerExcursion?.link?.url ?? ''
                  : ''
              }
            />
          );
        }
      })}
    </>
  );
}
